import React from 'react';
import ReactMarkdown from 'react-markdown';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * MarkdownRenderer component
 * Renders markdown with basic formatting only (bold, italic, headings, lists, line breaks)
 * Blocks all links for security
 */
const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '' }) => {
  return (
    <div className={className}>
      <ReactMarkdown
        // Disable links entirely
        components={{
          // Block anchor tags
          a: ({ children }) => <span>{children}</span>,
          // Only allow basic elements
          img: () => null, // Block images
          code: ({ children }) => <span className="font-mono text-sm bg-gray-100 px-1 rounded">{children}</span>,
          pre: ({ children }) => <div className="font-mono text-sm bg-gray-100 p-2 rounded my-2">{children}</div>,
          // Style headings
          h1: ({ children }) => <h1 className="text-2xl font-bold mt-4 mb-2">{children}</h1>,
          h2: ({ children }) => <h2 className="text-xl font-bold mt-3 mb-2">{children}</h2>,
          h3: ({ children }) => <h3 className="text-lg font-semibold mt-3 mb-1">{children}</h3>,
          h4: ({ children }) => <h4 className="text-base font-semibold mt-2 mb-1">{children}</h4>,
          h5: ({ children }) => <h5 className="text-sm font-semibold mt-2 mb-1">{children}</h5>,
          h6: ({ children }) => <h6 className="text-sm font-semibold mt-2 mb-1">{children}</h6>,
          // Style lists
          ul: ({ children }) => <ul className="list-disc list-inside my-2 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-inside my-2 space-y-1">{children}</ol>,
          li: ({ children }) => <li className="ml-4">{children}</li>,
          // Style paragraphs
          p: ({ children }) => <p className="my-2">{children}</p>,
          // Style emphasis
          strong: ({ children }) => <strong className="font-bold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          // Block other potentially dangerous elements
          blockquote: ({ children }) => <div className="border-l-4 border-gray-300 pl-4 my-2 italic">{children}</div>,
        }}
        // Disable HTML in markdown to prevent XSS
        disallowedElements={['script', 'iframe', 'object', 'embed', 'link', 'style']}
        unwrapDisallowed={true}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;

